import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { corporateTheme } from '../themes/corporate';

const FooterContainer = styled.footer`
  width: 100%;
  background: linear-gradient(135deg, ${corporateTheme.backgroundSecondary} 0%, ${corporateTheme.backgroundTertiary} 100%);
  border-top: 1px solid ${corporateTheme.cardBorder};
  padding: 4rem 2rem 2rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const LogoSection = styled(motion.div)`
  max-width: 400px;
`;

const Logo = styled.div`
  font-size: 2rem;
  font-weight: 800;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
`;

const Description = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(30, 64, 175, 0.1);
  border-radius: 50%;
  color: ${corporateTheme.primary};
  transition: all 0.3s ease;
  font-size: 0.8rem;
  font-weight: 600;
  
  &:hover {
    background: rgba(30, 64, 175, 0.2);
    transform: translateY(-2px);
  }
`;

const FooterSection = styled(motion.div)``;

const SectionTitle = styled.h3`
  color: ${corporateTheme.textPrimary};
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 0.8rem;
`;

const Link = styled.a`
  color: ${corporateTheme.textTertiary};
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${corporateTheme.primary};
  }
`;

const StyledRouterLink = styled(RouterLink)`
  color: ${corporateTheme.textTertiary};
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${corporateTheme.primary};
  }
`;

const FooterBottom = styled.div`
  padding-top: 2rem;
  border-top: 1px solid ${corporateTheme.cardBorder};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Copyright = styled.div`
  color: ${corporateTheme.textTertiary};
  font-size: 0.9rem;
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Footer = () => {
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <LogoSection variants={itemVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Logo>GrowTech</Logo>
            <Description>
              Leading digital affiliate network specializing in Eastern Asian markets.
              Maximize your revenue with our premium e-commerce and SaaS partnerships.
            </Description>
            <SocialLinks>
              <SocialLink href="#" whileHover={{ scale: 1.1 }}>FB</SocialLink>
              <SocialLink href="#" whileHover={{ scale: 1.1 }}>TW</SocialLink>
              <SocialLink href="#" whileHover={{ scale: 1.1 }}>IG</SocialLink>
              <SocialLink href="#" whileHover={{ scale: 1.1 }}>LN</SocialLink>
            </SocialLinks>
          </LogoSection>

          <FooterSection variants={itemVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionTitle>Services</SectionTitle>
            <FooterLinks>
              <FooterLink><Link href="/#services">Our Services</Link></FooterLink>
              <FooterLink><Link href="/#features">Platform Features</Link></FooterLink>
              <FooterLink><Link href="/#stats">Commission Rates</Link></FooterLink>
              <FooterLink><StyledRouterLink to="/contact">Get Started</StyledRouterLink></FooterLink>
            </FooterLinks>
          </FooterSection>

          <FooterSection variants={itemVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionTitle>Company</SectionTitle>
            <FooterLinks>
              <FooterLink><StyledRouterLink to="/about">About Us</StyledRouterLink></FooterLink>
              <FooterLink><Link href="/#testimonials">Success Stories</Link></FooterLink>
              <FooterLink><StyledRouterLink to="/contact">Contact</StyledRouterLink></FooterLink>
              <FooterLink><Link href="/#why-choose">Why Choose Us</Link></FooterLink>
            </FooterLinks>
          </FooterSection>

          <FooterSection variants={itemVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionTitle>Support</SectionTitle>
            <FooterLinks>
              <FooterLink><StyledRouterLink to="/contact">Contact Support</StyledRouterLink></FooterLink>
              <FooterLink><StyledRouterLink to="/privacy">Privacy Policy</StyledRouterLink></FooterLink>
              <FooterLink><StyledRouterLink to="/terms">Terms of Service</StyledRouterLink></FooterLink>
              <FooterLink><Link href="mailto:info@growtech.cc">Email Us</Link></FooterLink>
            </FooterLinks>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <Copyright>
            © 2024 GrowTech. All rights reserved. Licensed digital affiliate network.
          </Copyright>
          <LegalLinks>
            <StyledRouterLink to="/privacy">Privacy Policy</StyledRouterLink>
            <StyledRouterLink to="/terms">Terms of Service</StyledRouterLink>
            <Link href="#">Cookie Policy</Link>
          </LegalLinks>
        </FooterBottom>
      </Container>
    </FooterContainer>
  );
};

export default Footer; 